import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';

import { MISCELLANEOUS_COMPETENCY_ID } from 'constants/ProjectConstants';

// creating a context for easy prop passing.

const defaultReview = {
  // helper objects to avoid prop drilling
  /*
    competancyInfo provides basic control and info for competancy side panel
  */
  competancyInfo: {
    isOpen: false,
    title: '',
    id: '',
    empId: null,
  },
  /*
    peerFeedback is used to store and retrieve info about selected question 
    in manager view. 
      - questionId is unique for each question and is used to identify selected question.
      - answerType is used in peerFeedback comp to display peer's responses as per answerType
  */
  peerFeedback: {
    goalId: null,
    competencyId: null,
    questionId: null,
    answerType: null,
  },
  /*
    selectedReviewInfo is used to store and retrieve info about a particular review 
    - reviewId, employeeId is currently not being used in logic
    - submissionStatus is used to enable / disable form editing.
  */
  selectedReviewInfo: {
    reviewId: null,
    employeeId: null,
    submissionStatus: null,
    isSubmitted: null,
    reviewProviderType: null, // can be 'SELF', 'MANAGER'
    reviewStage: null,
    reviewName: null,
    providerEmployeeId: null,
    employeeName: null,
    reviewCycleName: null,
    isPublished: null,
    isShowRatingPanel: null,
  },
  isDueDatePassed: null,
};

const ReviewContext = createContext(defaultReview);

const competancyActions = {
  EXPAND_COMPETANCY_PANEL: 'EXPAND_COMPETANCY_PANEL',
  CLOSE_COMPETANCY_PANEL: 'CLOSE_COMPETANCY_PANEL',
};

const peerFeedbackActions = {
  SET_SELECETED_QUESTION_ID: 'SET_SELECETED_QUESTION_ID',
  CLEAR_SELECTED_QUESTION_ID: 'CLEAR_SELECTED_QUESTION_ID',
};

const selectedReviewInfoActions = {
  SET_SELECTED_REVIEW_DETAILS: 'SET_SELECTED_REVIEW_DETAILS',
  SET_SELECTED_REVIEW_SUBMISSION_STATUS: 'SET_SELECTED_REVIEW_SUBMISSION_STATUS',
  CLEAR_SELECTED_REVIEW_DETAILS: 'CLEAR_SELECTED_REVIEW_DETAILS',
};

const competancyActionsReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case competancyActions.EXPAND_COMPETANCY_PANEL:
      return {
        ...state,
        competancyInfo: {
          isOpen: true,
          id: payload.id,
          title: payload.title,
          empId: payload.empId,
        },
      };
    case competancyActions.CLOSE_COMPETANCY_PANEL:
      return {
        ...state,
        competancyInfo: {
          isOpen: false,
          id: '',
          title: '',
        },
      };
    default:
      return state;
  }
};

const peerFeedbackActionsReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case peerFeedbackActions.SET_SELECETED_QUESTION_ID:
      return {
        ...state,
        peerFeedback: {
          goalId: payload.goalId,
          competencyId: payload.competencyId,
          questionId: payload.questionId,
          answerType: payload.answerType,
          answerOptions: payload.answerOptions,
        },
      };
    case peerFeedbackActions.CLEAR_SELECTED_QUESTION_ID:
      return {
        ...state,
        peerFeedback: {
          goalId: null,
          competencyId: null,
          questionId: null,
          answerType: null,
          answerOptions: null,
        },
      };
    default:
      return state;
  }
};

const selectedReviewInfoActionsReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case selectedReviewInfoActions.SET_SELECTED_REVIEW_DETAILS:
      return {
        ...state,
        isDueDatePassed: payload.isDueDatePassed,
        selectedReviewInfo: {
          reviewId: payload.reviewId,
          employeeId: payload.employeeId,
          submissionStatus: payload.submissionStatus,
          isSubmitted:
            payload.submissionStatus === 'SUBMITTED' ||
            payload.submissionStatus === 'ACKNOWLEDGED' ||
            payload.isDueDatePassed,
          reviewProviderType: payload.reviewProviderType,
          reviewStage: payload.reviewStage,
          reviewName: payload.reviewName,
          providerEmployeeId: payload.providerEmployeeId,
          employeeName: payload.employeeName,
          reviewCycleName: payload.reviewCycleName,
          isPublished: payload.isPublished,
          isShowRatingPanel: payload.isShowRatingPanel ?? null,
        },
      };
    case selectedReviewInfoActions.CLEAR_SELECTED_REVIEW_DETAILS:
      return {
        ...state,
        selectedReviewInfo: {
          reviewId: null,
          employeeId: null,
          isSubmitted: null,
          submissionStatus: null,
          reviewProviderType: null,
          providerEmployeeId: null,
          employeeName: null,
          reviewCycleName: null,
          isPublished: null,
          isShowRatingPanel: null,
          isDueDatePassed: null,
        },
      };
    case selectedReviewInfoActions.SET_SELECTED_REVIEW_SUBMISSION_STATUS:
      return {
        ...state,
        isDueDatePassed: payload.isDueDatePassed,
        selectedReviewInfo: {
          ...state.selectedReviewInfo,
          submissionStatus: payload.submissionStatus,
          isSubmitted:
            payload.submissionStatus === 'SUBMITTED' ||
            payload.submissionStatus === 'ACKNOWLEDGED' ||
            payload.isDueDatePassed,
        },
      };

    default:
      return state;
  }
};

const combinedReducer = (state, action) => {
  const { type } = action;

  if (peerFeedbackActions[type]) {
    return peerFeedbackActionsReducer(state, action);
  }
  if (competancyActions[type]) {
    return competancyActionsReducer(state, action);
  }
  if (selectedReviewInfoActions[type]) {
    return selectedReviewInfoActionsReducer(state, action);
  }
  return null;
};

export const ReviewProvider = ({ children }) => {
  const [reviewState, reviewDispatch] = useReducer(combinedReducer, defaultReview);

  // methods to update competancy details inside context.

  /**
   * @deprecated Please use redux action openCompetencySidePanelAction
   */
  const showCompetancyDetailsPanel = useCallback((competancyId, competencyTitle = '', employeeId = null) => {
    reviewDispatch({
      type: competancyActions.EXPAND_COMPETANCY_PANEL,
      payload: {
        id: competancyId || MISCELLANEOUS_COMPETENCY_ID,
        title: competencyTitle,
        empId: employeeId,
      },
    });
  }, []);

  /**
   * @deprecated Please use redux action closeCompetencySidePanelAction
   */
  const closeCompetancyDetailsPanel = useCallback(() => {
    reviewDispatch({
      type: competancyActions.CLOSE_COMPETANCY_PANEL,
    });
  }, []);

  // functions to update peerFeedback question id inside context.

  const showPeerFeedback = useCallback(({ goalId, competencyId, questionId, answerType, answerOptions = [] }) => {
    reviewDispatch({
      type: peerFeedbackActions.SET_SELECETED_QUESTION_ID,
      payload: {
        goalId,
        competencyId,
        questionId,
        answerType,
        answerOptions,
      },
    });
  }, []);

  const clearPeerFeedback = useCallback(() => {
    reviewDispatch({
      type: peerFeedbackActions.CLEAR_SELECTED_QUESTION_ID,
    });
  }, []);

  // functions to update selected Review details
  const setSelectedReviewDetails = useCallback(
    ({
      reviewId,
      employeeId,
      submissionStatus,
      reviewProviderType = 'SELF',
      reviewStage = 'NOT_PUBLISHED',
      reviewName,
      providerEmployeeId,
      employeeName,
      reviewCycleName,
      isPublished,
      isShowRatingPanel = null,
      isDueDatePassed,
    }) => {
      reviewDispatch({
        type: selectedReviewInfoActions.SET_SELECTED_REVIEW_DETAILS,
        payload: {
          reviewId,
          employeeId,
          submissionStatus,
          reviewProviderType,
          reviewStage,
          reviewName,
          providerEmployeeId,
          employeeName,
          reviewCycleName,
          isPublished,
          isShowRatingPanel,
          isDueDatePassed,
        },
      });
    }
  );

  // functions to update selected Review submissionStatus
  const setSelectedReviewSubmissionStatus = useCallback(({ submissionStatus }) => {
    reviewDispatch({
      type: selectedReviewInfoActions.SET_SELECTED_REVIEW_SUBMISSION_STATUS,
      payload: {
        submissionStatus,
      },
    });
  });

  const clearSelectedReviewDetails = useCallback(() => {
    reviewDispatch({
      type: selectedReviewInfoActions.CLEAR_SELECTED_REVIEW_DETAILS,
    });
  });

  const contextValue = useMemo(
    () => ({
      state: reviewState,
      showCompetancyDetailsPanel,
      closeCompetancyDetailsPanel,
      showPeerFeedback,
      clearPeerFeedback,
      setSelectedReviewDetails,
      setSelectedReviewSubmissionStatus,
      clearSelectedReviewDetails,
    }),
    [
      clearPeerFeedback,
      clearSelectedReviewDetails,
      closeCompetancyDetailsPanel,
      reviewState,
      setSelectedReviewDetails,
      setSelectedReviewSubmissionStatus,
      showCompetancyDetailsPanel,
      showPeerFeedback,
    ]
  );

  return <ReviewContext.Provider value={contextValue}>{children}</ReviewContext.Provider>;
};

export default ReviewContext;

export const useReview = () => useContext(ReviewContext);
